import { Link as RouterLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Grid,
  Link,
  TextField,
  Typography,
  Container
} from '@material-ui/core';
import useAuth from '../../../hooks/useAuth';
import countries from '../account/countries';
import { Helmet } from 'react-helmet-async';
import useSettings from '../../../hooks/useSettings';
import { useLocation } from 'react-router';
import useMounted from '../../../hooks/useMounted';
import { useCallback, useEffect, useState } from 'react';
import { usersApi } from '../../../api/usersApi';
import PhotoUploadDialog from './PhotoUploadDialog';

const UserGeneralSettings = (props) => {
  const path = useLocation();
  const routeUserId = path.pathname.split('/')[3];
  const [userToUpdate, setUserToUpdate] = useState({});
  const { user } = useAuth();
  const effectiveUserId = props.userId || routeUserId || (user ? user.id : null);
  const { settings } = useSettings();
  const mounted = useMounted();
  // Removed unused newUser and isEditForm states
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);

  const getUserInfos = useCallback(async () => {
    try {
      if (!effectiveUserId) return;
      const data = await usersApi.getUserInfos(window.localStorage.getItem('accessToken'), effectiveUserId);
      if (mounted.current) {
        if (routeUserId !== "new") {
          setUserToUpdate(data.data);
          // Edit mode
        } else {
          const newData = {
            city: ' ',
            country: 'FR',
            email: ' ',
            firstname: ' ',
            lastname: ' ',
            phone: ' ',
            region: ' ',
            adress: ' ',
            zip: ' ',
            birthday: ' ',
            personal_email: ' ',
            submit: null
          };
          setUserToUpdate(newData);
          // Create mode
        }
      }

    } catch (err) {
      console.error(err);
    }
  }, [mounted, effectiveUserId, routeUserId]);

  useEffect(() => {
    getUserInfos();
  }, [getUserInfos]);

  const baseSchema = Yup.object().shape({
    city: Yup.string().max(255),
    country: Yup.string().max(255).nullable(),
    email: Yup
      .string()
      .email('Doit être une adresse email valide')
      .max(255)
      .required('L\'email est requis'),
    firstname: Yup
      .string()
      .max(255)
      .required('Le prénom est requis'),
    lastname: Yup
      .string()
      .max(255)
      .required('Le nom est requis'),
    phone: Yup.string().max(20),
    region: Yup.string(),
    adress: Yup.string(),
    zip: Yup.string(),
    personal_email: Yup
      .string()
      .email('Doit être une adresse email valide')
      .max(255),
    birthday: Yup.date(),
    password: Yup.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
    confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
  });

  const inputChangeHandler = (e, key) => {
    const { value } = e.target;
    setUserToUpdate((prevState) => {
      return {...prevState, [key] : value};
    });
  }

  const dropdownChangeHandler = (newValue, key) => {
    setUserToUpdate((prevState) => {
      return {...prevState, [key] : newValue};
    });
  }

  return (
    <>
      <Helmet>
        <title>Tableau de bord: Profil de l&apos;utilisateur | Syndays</title>
      </Helmet>
      {(user.id_service === 0 || user.id === effectiveUserId) && (
        <Box
          sx={{
            backgroundColor: 'background.default',
            minHeight: '100%',
          }}
        >
          <Container maxWidth={settings.compact ? 'xl' : false}>
            <Grid
              container
              spacing={3}
              sx={{ mt: 1 }}
              {...props}
            >
              <Grid
                item
                lg={4}
                md={6}
                xl={3}
                xs={12}
              >
                <Card>
                  <CardContent>
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          border: (theme) => `1px dashed ${theme.palette.divider}`,
                          borderRadius: '50%'
                        }}
                      >
                        
                        <Avatar
                          src={userToUpdate.photo ? `data:image/jpeg;base64,${userToUpdate.photo}` : ''}
                          sx={{
                            height: 140,
                            width: 140
                          }}
                        />
                      </Box>
                      <Typography
                        color="textPrimary"
                        sx={{ mt: 1 }}
                        variant="subtitle2"
                      >
                        {(userToUpdate.firstname) ? userToUpdate.firstname : ' '}
                        {' '}
                        {(userToUpdate.lastname) ? userToUpdate.lastname : ''}
                      </Typography>
                      <Typography
                        color="textSecondary"
                        variant="body2"
                      >
                        {userToUpdate.functions}
                        <Link
                          color="primary"
                          component={RouterLink}
                          to="/dashboard/account"
                        >
                        </Link>
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      color="primary"
                      fullWidth
                      variant="text"
                      onClick={() => setPhotoDialogOpen(true)}
                    >
                      {(routeUserId !== "new") ? "Changer de photo" : "Charger une photo"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              <Grid
                item
                lg={8}
                md={6}
                xl={9}
                xs={12}
              >
                <Formik
                  enableReinitialize
                  initialValues={{
                    city: userToUpdate.city || '',
                    country: userToUpdate.country || 'FR',
                    email: userToUpdate.email || '',
                    firstname: userToUpdate.firstname || '',
                    lastname: userToUpdate.lastname || '',
                    phone: userToUpdate.phone || '',
                    region: userToUpdate.region || '',
                    adress: userToUpdate.adress || '',
                    zip: userToUpdate.zip || '',
                    birthday: userToUpdate.birthday || '',
                    personal_email: userToUpdate.personal_email || '',
                    password: '',
                    confirmPassword: '',
                    submit: null
                  }}
                  validationSchema={baseSchema}
                  onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
                    try {
                      // Custom validation for passwords
                      if (routeUserId === 'new' && (!values.password || !values.confirmPassword)) {
                        setErrors({
                          password: 'Le mot de passe est requis',
                          confirmPassword: 'La confirmation du mot de passe est requise'
                        });
                        setSubmitting(false);
                        return;
                      }
                      await usersApi.updateUser(window.localStorage.getItem('accessToken'), values, effectiveUserId);
                      const data = await usersApi.getUserInfos(window.localStorage.getItem('accessToken'), effectiveUserId);
                      setUserToUpdate(data.data);
                      resetForm();
                      setStatus({ success: true });
                      setSubmitting(false);
                      toast.success('Profil mis à jour !');
                    } catch (err) {
                      console.error(err);
                      toast.error('Erreur lors de la mise à jour');
                      setStatus({ success: false });
                      setErrors({ submit: err.message });
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
                    <form onSubmit={handleSubmit}>
                      <Card>
                        <CardHeader title="Profil" />
                        <Divider />
                        <CardContent>
                          <Grid
                            container
                            spacing={4}
                          >
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.firstname && errors.firstname)}
                                fullWidth
                                helperText={touched.firstname && errors.firstname}
                                label="Prénom"
                                name="firstname"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'firstname')}
                                value={userToUpdate?.firstname || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.lastname && errors.lastname)}
                                fullWidth
                                helperText={touched.lastname && errors.lastname}
                                label="Nom"
                                name="lastname"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'lastname')}
                                value={userToUpdate?.lastname || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.email && errors.email)}
                                fullWidth
                                helperText={touched.email && errors.email
                                  ? errors.email
                                  : 'Ceci est l\'adresse email de contact.'}
                                label="Adresse Email"
                                name="email"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'email')}
                                value={userToUpdate?.email || ''}
                                type="email"

                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            {routeUserId === 'new' && (
                              <>
                                <Grid
                                  item
                                  md={6}
                                  xs={12}
                                >
                                  <TextField
                                    error={Boolean(touched.password && errors.password)}
                                    fullWidth
                                    helperText={touched.password && errors.password}
                                    label="Mot de passe"
                                    name="password"
                                    onBlur={handleBlur}
                                                                    onChange={(e) => inputChangeHandler(e, 'password')}
                                value={userToUpdate?.password || ''}
                                    type="password"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid
                                  item
                                  md={6}
                                  xs={12}
                                >
                                  <TextField
                                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                    fullWidth
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    label="Confirmez le mot de passe"
                                    name="confirmPassword"
                                    onBlur={handleBlur}
                                                                    onChange={(e) => inputChangeHandler(e, 'confirmPassword')}
                                value={userToUpdate?.confirmPassword || ''}
                                    type="password"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </>
                            )}
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.phone && errors.phone)}
                                fullWidth
                                helperText={touched.phone && errors.phone}
                                label="Numéro de téléphone"
                                name="phone"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'phone')}
                                value={userToUpdate?.phone || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.city && errors.city)}
                                fullWidth
                                helperText={touched.city && errors.city}
                                label="Ville"
                                name="city"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'city')}
                                value={userToUpdate?.city || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <Autocomplete
                                fullWidth
                                options={countries}
                                getOptionLabel={(option) => option.text}
                                value={userToUpdate.country || 'FR'}
                                onChange={(e, newValue) => dropdownChangeHandler(newValue?.value, 'country')}
                                // value={countries.find(
                                //   (country) => country.code === values.country
                                // )}
                                // onChange={(event, newValue) => {
                                //   handleChange({
                                //     target: {
                                //       name: 'country',
                                //       value: newValue ? newValue.code : ''
                                //     }
                                //   });
                                // }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Pays"
                                    name="country"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.region && errors.region)}
                                fullWidth
                                helperText={touched.region && errors.region}
                                label="Région"
                                name="region"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'region')}
                                value={userToUpdate?.region || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.adress && errors.adress)}
                                fullWidth
                                helperText={touched.adress && errors.adress}
                                label="Adresse"
                                name="adress"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'adress')}
                                value={userToUpdate?.adress || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.zip && errors.zip)}
                                fullWidth
                                helperText={touched.zip && errors.zip}
                                label="Code Postal"
                                name="zip"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'zip')}
                                value={userToUpdate?.zip || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.birthday && errors.birthday)}
                                fullWidth
                                helperText={touched.birthday && errors.birthday}
                                label="Date de naissance"
                                name="birthday"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'birthday')}
                                value={userToUpdate?.birthday || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                type="date"
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.personal_email && errors.personal_email)}
                                fullWidth
                                helperText={touched.personal_email && errors.personal_email}
                                label="Email personnel"
                                name="personal_email"
                                onBlur={handleBlur}
                                onChange={(e) => inputChangeHandler(e, 'personal_email')}
                                value={userToUpdate?.personal_email || ''}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                type="email"
                              />
                            </Grid>

                          </Grid>
                          {errors.submit && (
                            <Box sx={{ mt: 3 }}>
                              <FormHelperText error>
                                {errors.submit}
                              </FormHelperText>
                            </Box>
                          )}
                        </CardContent>
                        <Divider />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 2
                          }}
                        >
                          <Button
                            color="primary"
                            disabled={props.isSubmitting}
                            type="submit"
                            variant="contained"
                          >
                            {(routeUserId !== "new") ? "Mettre à jour" : "Créer"}
                          </Button>
                        </Box>
                      </Card>
                    </form>
                  )}
                </Formik>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      <PhotoUploadDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        onPhotoUploaded={(photoData) => {
          setUserToUpdate(prev => ({
            ...prev,
            photo: photoData.split(',')[1] // Enlever le préfixe data:image/...
          }));
        }}
        currentPhoto={userToUpdate.photo ? `data:image/jpeg;base64,${userToUpdate.photo}` : ''}
        userId={effectiveUserId}
      />
    </>
  );
};

export default UserGeneralSettings;
